import {computed, defineComponent, onMounted, ref} from "vue";
import * as KlipApi from "@/api/klip-api.proxy";
import { useRoute } from '@/router/router-vue3';
import { IUnaZone } from "./una-zone-model";
import { AdminSubOrganisationDetailUnaZone, AdminSubOrganisationDetailUnaKlimZone } from "@/app/admin/admin-routes";
import {IDrawZoneMapGeometry} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map';
import KlDrawZoneMap from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-map.vue';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useForm} from 'vee-validate';
import {RouteRecordRaw} from 'vue-router';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlSubOrganisationUna',
    props: {
        organisation: {
            type: KlipApi.SubOrganisationDetail,
            default: null,
            required: true
        }
    },
    emits: ['has-zones', 'loading-zones'],
    setup(props, { emit }) {

        const schema = yup.object({
            confirmation: yup.boolean().notRequired().label('Nieuwe planaanvraag te bevestigen'),
        });

        const proxy = useKlipApiProxy();

        const klbZonesMap = ref<InstanceType<typeof KlDrawZoneMap>>();

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: { confirmation: props.organisation.unaData.confirmation },
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const disableSubmit = computed((): boolean => {
            return !form.meta.value.valid || !form.meta.value.dirty;
        });

        const confirmation = fields.confirmation.getValue<boolean>();


        const displayName = computed((): string => props.organisation.displayName);
        const confirmationExampleUri = computed((): string => props.organisation.unaData.confirmationExampleUri);
        const subjectSigningCertificate = computed((): string => props.organisation.subjectSigningCertificate);

        const route = useRoute();
        const zonesFetching = ref<boolean>(true);
        const isSending = ref<boolean>(false);
        const alsoSearchArchivedZones = ref<boolean>(false);
        const zones = ref<IUnaZone[]>([]);
        const routeUnaKlimZone = ref<RouteRecordRaw>(AdminSubOrganisationDetailUnaKlimZone);
        const routeUnaZone = ref<RouteRecordRaw>(AdminSubOrganisationDetailUnaZone);

        const drawZones = computed(() => {
            const today = new Date();

            const myNamespaces = zones.value;
            const allDrawZones = myNamespaces.map((zone, index): IDrawZoneMapGeometry => {

                const activationDate = new Date(zone.zoneGeometryActivationDate);
                const isActive = !zone.isArchived && (zone.isKlimZone || !zone.zoneGeometryActivationDate || (activationDate < today));

                return {
                    name: zone.zoneName,
                    wkt: zone.zoneGeometry,
                    orgZoneInfo: {
                        isKlim: zone.isKlimZone,
                        guid: zone.zoneId,
                        namespace: zone.namespace,
                        isActive: isActive,
                        isArchived: zone.isArchived,
                        willBeActivatedOn: !isActive ? activationDate : undefined,
                        editLink: {
                            name: zone.link?.route?.name,
                            params: { zoneId: zone.zoneId },
                        },
                    }
                }
            });

            let filteredDrawZones = allDrawZones;
            if (!alsoSearchArchivedZones.value) {
                filteredDrawZones = allDrawZones.filter((cz: IDrawZoneMapGeometry): boolean => !cz.orgZoneInfo.isArchived);
            }
            return filteredDrawZones;
        });

        const search = async (value: boolean) => {
            alsoSearchArchivedZones.value = value;
            await _loadZones();
        };

        const _loadZones = async () => {
            zonesFetching.value = true;
            emit('loading-zones', true);
            const unaZones = await proxy.organisation_LoadUnaZonesForOrganisation(props.organisation.organisationId, alsoSearchArchivedZones.value);
            zones.value = unaZones.result
                .filter((item) => item.zoneId !== route.params.removedItem)
                .map((zone): IUnaZone => ({
                    zoneId: zone.zoneId,
                    zoneName: zone.zoneName,
                    namespace: zone.namespace,
                    isKlimZone: zone.isKlimZone,
                    unaId: zone.unaId,
                    zoneGeometryActivationDate: zone.zoneGeometryActivationDate,
                    zoneGeometry: zone.zoneGeometry,
                    isArchived: zone.isArchived,
                    link: {
                        route: zone.isKlimZone ? routeUnaKlimZone : routeUnaZone,
                        params: {
                            organisationId: props.organisation.organisationId,
                            zoneId: zone.zoneId,
                        },
                    },
                }));

            emit('has-zones', zones.value.length > 0);
            zonesFetching.value = false;
            emit('loading-zones', false);
        }

        const _updateConfirmationInUnaZoneSettings = async () => {
            isSending.value = true;

            const input = KlipApi.UpdateConfirmationInUnaZoneSettingsInput.fromJS({
                unaId: props.organisation.unaData.unaId,
                confirmation: confirmation.value,
            });

            await proxy.operations_UpdateConfirmationInUnaZoneSettings(input);

            form.resetForm({ values: form.values });

            isSending.value = false;
        }

        onMounted(async () => {
            await _loadZones();
        });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_updateConfirmationInUnaZoneSettings, _onInvalidSubmit);

        return {
            confirmation,

            klbZonesMap,

            displayName,
            isSending,
            confirmationExampleUri,
            subjectSigningCertificate,
            zones,
            zonesFetching,
            search,

            drawZones,
            alsoSearchArchivedZones,

            onSubmit,
            disableSubmit,

            fields,
        };
    }
});

